import { useOperators } from 'dashboard/components-next/filter/operators';
import { useMapGetter } from 'dashboard/composables/store.js';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { MODEL_NAMES } from './constants';

const AUDIT_TRAIL_ATTRIBUTES = {
  USER: 'user',
  MODEL: 'model',
  ID: 'id',
  DATE: 'date',
  IP_ADDRESS: 'ip_address',
  ACTION: 'action',
};

export function useAuditTrailFilterContext() {
  const { t } = useI18n();
  const { equalityOperators, containmentOperators, dateOperators } =
    useOperators();
  const agents = useMapGetter('agents/getAgents');

  const auditTrailDateOperators = computed(() => [
    equalityOperators.value.find(op => op.value === 'equal_to'),
    ...dateOperators.value.filter(op => op.value !== 'days_before'),
  ]);

  const agentOptions = computed(() => {
    const systemUserOption = {
      id: null,
      name: t('WGPT_AUDIT_TRAIL.COMMON.SYSTEM_USER'),
    };

    if (!agents.value?.length) {
      return [systemUserOption];
    }

    return [
      systemUserOption,
      ...agents.value.map(agent => ({
        id: agent.id,
        name: agent.name || agent.email || `Agent ${agent.id}`,
      })),
    ];
  });

  const modelNameOptions = MODEL_NAMES.map(modelName => {
    const snakeCaseKey = useSnakeCase({ [modelName]: true });
    const [name] = Object.keys(snakeCaseKey);
    return {
      id: modelName,
      // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
      name: t(`WGPT_AUDIT_TRAIL.MODELS.${name.toUpperCase()}`),
    };
  }).sort((a, b) => a.name.localeCompare(b.name));

  const actionOptions = [
    { id: 'create', name: t('WGPT_AUDIT_TRAIL.COMMON.CREATE') },
    { id: 'update', name: t('WGPT_AUDIT_TRAIL.COMMON.UPDATE') },
    { id: 'destroy', name: t('WGPT_AUDIT_TRAIL.COMMON.DESTROY') },
    {
      id: 'sign_in',
      name: t('WGPT_AUDIT_TRAIL.COMMON.SIGN_IN'),
    },
    {
      id: 'sign_out',
      name: t('WGPT_AUDIT_TRAIL.COMMON.SIGN_OUT'),
    },
  ];

  const filterTypes = computed(() => [
    {
      attributeKey: AUDIT_TRAIL_ATTRIBUTES.USER,
      value: AUDIT_TRAIL_ATTRIBUTES.USER,
      attributeName: t('WGPT_AUDIT_TRAIL.FILTER.USER'),
      label: t('WGPT_AUDIT_TRAIL.FILTER.USER'),
      inputType: 'searchSelect',
      options: agentOptions.value,
      dataType: 'text',
      filterOperators: equalityOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: AUDIT_TRAIL_ATTRIBUTES.ACTION,
      value: AUDIT_TRAIL_ATTRIBUTES.ACTION,
      attributeName: t('WGPT_AUDIT_TRAIL.FILTER.ACTION'),
      label: t('WGPT_AUDIT_TRAIL.FILTER.ACTION'),
      inputType: 'searchSelect',
      options: actionOptions,
      dataType: 'text',
      filterOperators: equalityOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: AUDIT_TRAIL_ATTRIBUTES.MODEL,
      value: AUDIT_TRAIL_ATTRIBUTES.MODEL,
      attributeName: t('WGPT_AUDIT_TRAIL.FILTER.MODEL'),
      label: t('WGPT_AUDIT_TRAIL.FILTER.MODEL'),
      inputType: 'searchSelect',
      options: modelNameOptions,
      dataType: 'text',
      filterOperators: equalityOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: AUDIT_TRAIL_ATTRIBUTES.ID,
      value: AUDIT_TRAIL_ATTRIBUTES.ID,
      attributeName: t('WGPT_AUDIT_TRAIL.FILTER.MODEL_WITH_ID'),
      label: t('WGPT_AUDIT_TRAIL.FILTER.MODEL_WITH_ID'),
      inputType: 'plainText',
      options: [],
      dataType: 'text',
      filterOperators: equalityOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: AUDIT_TRAIL_ATTRIBUTES.DATE,
      value: AUDIT_TRAIL_ATTRIBUTES.DATE,
      attributeName: t('WGPT_AUDIT_TRAIL.FILTER.DATE'),
      label: t('WGPT_AUDIT_TRAIL.FILTER.DATE'),
      inputType: 'date',
      options: [],
      dataType: 'text',
      filterOperators: auditTrailDateOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: AUDIT_TRAIL_ATTRIBUTES.IP_ADDRESS,
      value: AUDIT_TRAIL_ATTRIBUTES.IP_ADDRESS,
      attributeName: t('WGPT_AUDIT_TRAIL.FILTER.IP_ADDRESS'),
      label: t('WGPT_AUDIT_TRAIL.FILTER.IP_ADDRESS'),
      inputType: 'plainText',
      options: [],
      dataType: 'text',
      filterOperators: containmentOperators.value,
      attributeModel: 'standard',
    },
  ]);

  return { filterTypes };
}
